import React, { useState, useCallback, useRef } from 'react';
import { FileText, Image, Sparkles, Loader2 } from 'lucide-react';
import type { ExportType } from '../types';

interface ExportTypeSelectorProps {
  visible: boolean;
  onSelect: (type: ExportType) => void;
  onCancel: () => void;
}

/**
 * Modal minimalista para escolher tipo de exportação
 * Segue o padrão UI/UX do projeto
 *
 * ✅ CORREÇÃO BUG DUPLO CLIQUE:
 * - Adicionado estado de loading com feedback visual
 * - Botões desabilitados após primeiro clique
 * - Debounce via ref para evitar ghost clicks
 */
export function ExportTypeSelector({ visible, onSelect, onCancel }: ExportTypeSelectorProps) {
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedType, setSelectedType] = useState<ExportType | null>(null);
  const hasClickedRef = useRef(false);

  // ✅ Handler com proteção contra double-click
  const handleSelect = useCallback((type: ExportType) => {
    // Log para diagnóstico
    console.log('[ExportTypeSelector] Click detected:', { type, hasClicked: hasClickedRef.current, isSelecting });

    // Proteção via ref (síncrona, sem delay)
    if (hasClickedRef.current) {
      console.log('[ExportTypeSelector] Click BLOCKED (hasClickedRef)');
      return;
    }
    hasClickedRef.current = true;

    // Feedback visual
    setIsSelecting(true);
    setSelectedType(type);

    // Chamar callback após breve delay para garantir feedback visual
    requestAnimationFrame(() => {
      onSelect(type);
    });
  }, [onSelect, isSelecting]);

  // Reset ao fechar/reabrir
  React.useEffect(() => {
    if (visible) {
      hasClickedRef.current = false;
      setIsSelecting(false);
      setSelectedType(null);
    }
  }, [visible]);

  if (!visible) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-navy-900/90 backdrop-blur-sm z-50"
        onClick={isSelecting ? undefined : onCancel}
      />

      {/* Modal - z-index maior que overlay + stopPropagation para evitar cliques fantasmas */}
      <div
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] bg-navy-800 rounded-lg shadow-xl z-[51] border border-navy-700/50"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-3 border-b border-navy-700">
          <h3 className="text-sm font-semibold text-white">Tipo de Exportação</h3>
          <p className="text-xs text-gray-400 mt-0.5">Escolha o que exportar:</p>
        </div>

        {/* Opções */}
        <div className="p-3 space-y-2">
          {/* Opção 1: Produção */}
          <button
            onClick={() => handleSelect('production')}
            disabled={isSelecting}
            className={`w-full flex items-start gap-2.5 p-2.5 rounded border transition-colors text-left group
              ${isSelecting && selectedType === 'production'
                ? 'bg-navy-600 border-navy-500'
                : isSelecting
                  ? 'bg-navy-700/30 border-navy-600/30 opacity-50 cursor-not-allowed'
                  : 'bg-navy-700/50 hover:bg-navy-700 border-navy-600/50 hover:border-navy-500'
              }`}
          >
            {isSelecting && selectedType === 'production' ? (
              <Loader2 className="w-4 h-4 text-white mt-0.5 flex-shrink-0 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 text-gray-400 group-hover:text-gray-300 mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white">Produção</div>
              <div className="text-xs text-gray-400 leading-snug mt-0.5">
                Arquivos de impressão (TIFF, PDF, EPS)
              </div>
            </div>
          </button>

          {/* Opção 2: Layout */}
          <button
            onClick={() => handleSelect('layout')}
            disabled={isSelecting}
            className={`w-full flex items-start gap-2.5 p-2.5 rounded border transition-colors text-left group
              ${isSelecting && selectedType === 'layout'
                ? 'bg-navy-600 border-navy-500'
                : isSelecting
                  ? 'bg-navy-700/30 border-navy-600/30 opacity-50 cursor-not-allowed'
                  : 'bg-navy-700/50 hover:bg-navy-700 border-navy-600/50 hover:border-navy-500'
              }`}
          >
            {isSelecting && selectedType === 'layout' ? (
              <Loader2 className="w-4 h-4 text-white mt-0.5 flex-shrink-0 animate-spin" />
            ) : (
              <Image className="w-4 h-4 text-gray-400 group-hover:text-gray-300 mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white">Layout</div>
              <div className="text-xs text-gray-400 leading-snug mt-0.5">
                PNGs para aprovação (escala 20%, 400 DPI)
              </div>
            </div>
          </button>

          {/* Opção 3: Completo (RECOMENDADO) */}
          <button
            onClick={() => handleSelect('both')}
            disabled={isSelecting}
            className={`w-full flex items-start gap-2.5 p-2.5 rounded border transition-colors text-left group relative
              ${isSelecting && selectedType === 'both'
                ? 'bg-yellow-400/30 border-yellow-400/60'
                : isSelecting
                  ? 'bg-yellow-400/5 border-yellow-400/20 opacity-50 cursor-not-allowed'
                  : 'bg-yellow-400/10 hover:bg-yellow-400/20 border-yellow-400/30 hover:border-yellow-400/50'
              }`}
          >
            {/* Badge Recomendado */}
            <div className="absolute -top-1.5 right-2 px-1.5 py-0.5 bg-yellow-400 text-navy-900 text-[10px] font-bold rounded">
              RECOMENDADO
            </div>

            {isSelecting && selectedType === 'both' ? (
              <Loader2 className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white">Completo</div>
              <div className="text-xs text-gray-400 leading-snug mt-0.5">
                Produção + Layout em sequência
              </div>
            </div>
          </button>
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-navy-700">
          <button
            onClick={onCancel}
            disabled={isSelecting}
            className={`w-full px-3 py-2 text-sm rounded transition-colors
              ${isSelecting
                ? 'bg-navy-700/50 text-gray-400 cursor-not-allowed'
                : 'bg-navy-700 hover:bg-navy-600 text-gray-200'
              }`}
          >
            {isSelecting ? 'Processando...' : 'Cancelar'}
          </button>
        </div>
      </div>
    </>
  );
}

